import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { useScreenContext } from '../hooks/useScreenContext';
import { useRecentWorkflows } from '../hooks/useRecentWorkflows';
import { WorkflowResult } from '../utils/workflowPolling';

export interface ProposedActionCard {
  /** The server-side AssistantActionModel row id (Phase 3a) — absent only for cards hydrated from a pre-Phase-3 stored conversation. */
  id?: string;
  /** The action's server-side status as of hydration (proposed/confirmed/executing/completed/failed/dismissed/paused) — absent for a card just proposed this session, which is implicitly 'proposed' until acted on. */
  status?: string;
  tool:
    | 'open_workflow'
    | 'navigate'
    | 'start_upload'
    | 'create_folder'
    | 'rename_folder'
    | 'delete_folder'
    | 'delete_document'
    | 'edit_customer'
    | 'create_customer'
    | 'show_documents'
    | 'rename_document'
    | 'move_documents'
    | 'open_report'
    | 'propose_plan'
    | 'save_memory'
    | 'create_task'
    | 'cancel_task'
    | 'commit_intake'
    | 'save_as_report'
    | 'download_file';
  args: Record<string, any>;
}

export interface ConfirmActionResult {
  status: 'confirmed' | 'executing' | 'completed' | 'failed';
  result?: Record<string, any>;
  error?: string;
}

export interface PlanStepView {
  tool: string;
  args: Record<string, any>;
  description: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped' | 'paused';
  result?: Record<string, any> | null;
  error?: string;
}

/**
 * Tracks a propose_plan card's live execution (Phase 3b) — same rationale as
 * ActiveWorkflowRun: lives here, not in the card component's local state, so
 * closing/reopening the Quick Start modal mid-plan doesn't lose progress or
 * invite a duplicate approval. `status: 'paused'` means the plan is waiting
 * on an interactive step (currently only start_upload).
 */
export interface ActivePlan {
  card: ProposedActionCard;
  title: string;
  steps: PlanStepView[];
  status: 'running' | 'paused' | 'completed' | 'failed';
}

// Cards whose args are shown persistently once rendered (they're an answer,
// not a live proposal) — kept in sync with AssistantConversation's own copy.
const INFORMATIONAL_CARDS = new Set(['show_documents', 'download_file']);

/**
 * Tracks an open_workflow card's execution across modal close/reopen. Lives
 * here (not in ActionCard's local state) because ActionCard unmounts when
 * the Quick Start modal closes, but the run itself continues server-side
 * (Phase 3a) regardless — without this, closing the modal mid-run silently
 * dropped the eventual result and the card reset to "Run it" on reopen,
 * inviting a duplicate confirm.
 */
export interface ActiveWorkflowRun {
  card: ProposedActionCard;
  status: 'running' | 'completed' | 'failed';
  result?: WorkflowResult;
  error?: string;
}

export interface QuickStartMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
  /** A proposed action card attached to this AI message — at most one live
   * (unconfirmed) card exists at a time; see `pendingCard`. */
  card?: ProposedActionCard;
  /** Transient "Searching customers…" style label while the turn is still
   * streaming tool activity — cleared once the turn resolves. */
  activity?: string;
  /** Files attached via the composer's paperclip/drop and sent alongside this
   * user message (SMART_INTAKE_PLAN.md Phase 3a) — a snapshot of
   * intakeAttachments at send time, kept for display only. */
  attachments?: IntakeAttachment[];
}

/** One file attached to the conversation's active intake session (SMART_INTAKE_PLAN.md Phase 3a). */
export interface IntakeAttachment {
  localId: string;
  name: string;
  status: 'uploading' | 'completed' | 'error';
  documentId?: string;
  errorMessage?: string;
}

export interface ConversationSummary {
  id: string;
  title: string | null;
  lastMessageAt: string | null;
  /** 'email' = an Agent Mailbox run (AGENT_MAILBOX_PLAN.md D6). */
  source?: 'chat' | 'email' | 'task';
  agentRunId?: string | null;
}

interface QuickStartAssistantContextType {
  messages: QuickStartMessage[];
  pendingCard: ProposedActionCard | null;
  sendMessage: (message: string) => Promise<void>;
  /** Appends a plain assistant note to the transcript (e.g. a client-side failure the server never saw) and persists it via the events endpoint. */
  appendAssistantNote: (content: string) => void;
  /** Mirrors a note the server already persisted (e.g. a confirm response) into the local message list, without a redundant events POST. */
  appendLocalNote: (content: string) => void;
  /** Injects a client-only follow-up card (no server action id) as the new pending card — for next steps the client can construct itself from a just-completed action's result, skipping a whole extra model turn. */
  presentFollowUpCard: (content: string, card: ProposedActionCard) => void;
  /** Executes a proposed action card server-side (Phase 3a). Mutating tools run and return their result; UI-only tools (navigate/show_documents/start_upload/open_report) just transition to 'confirmed'. */
  confirmAction: (actionId: string) => Promise<ConfirmActionResult>;
  /** Marks a proposed action card dismissed server-side. Fire-and-forget. */
  dismissAction: (actionId: string) => void;
  /** Clears the locally pending card; pass the card's id to also record the dismissal server-side. */
  dismissPendingCard: (actionId?: string) => void;
  /** Resets to a brand-new conversation — the next message starts a fresh server-side thread. */
  clear: () => void;
  /** Same reset, named for the conversation switcher. */
  startNewConversation: () => void;
  /** The current server-side conversation id, once one exists (after the first message). */
  conversationId: string | null;
  /** Recent conversations for this user — populate via loadConversations(). */
  conversations: ConversationSummary[];
  loadConversations: () => Promise<void>;
  /** Switches the active thread and hydrates its messages from the server. */
  switchConversation: (id: string) => Promise<void>;
  /** The in-flight/finished open_workflow run, if any — survives the card's own component unmounting. */
  activeRun: ActiveWorkflowRun | null;
  /** Starts (or re-reads) an open_workflow card's execution. Safe to call once per card; ActionCard gates the button on activeRun.status itself. */
  runOpenWorkflowCard: (card: ProposedActionCard) => void;
  /** The in-flight/finished propose_plan run, if any (Phase 3b) — survives the card's own component unmounting. */
  activePlan: ActivePlan | null;
  /** Approves a propose_plan card — steps run sequentially server-side, streaming live progress into activePlan. */
  approvePlanCard: (card: ProposedActionCard) => void;
  /** Continues a plan paused on an interactive step (e.g. after the user finishes uploading documents). */
  resumePlanCard: () => void;
  /** The active intake session id (SMART_INTAKE_PLAN.md Phase 3a) — set on the first file attached in chat, cleared once commit_intake completes or a new conversation starts. Sent alongside every turn so the server can inject its context block. */
  intakeSessionId: string | null;
  /** 'agency' grounds every turn on the company Knowledge Base (AGENCY_KNOWLEDGE_MODE_PLAN.md) — persisted in localStorage, sent per turn. */
  knowledgeMode: KnowledgeMode;
  setKnowledgeMode: (mode: KnowledgeMode) => void;
  /** Files attached to the CURRENT composer draft, not yet sent — cleared (and snapshotted onto the sent message) by sendMessage. */
  intakeAttachments: IntakeAttachment[];
  /** Starts (or reuses) an intake session and uploads the given files to it, tracking progress in intakeAttachments. */
  attachFilesToIntake: (files: File[]) => Promise<void>;
  /** Removes a not-yet-sent attachment from the composer and the session. */
  removeIntakeAttachment: (localId: string) => void;
  /** Clears the active intake session (e.g. after commit_intake completes) so the next attached file starts a fresh one. */
  clearIntakeSession: () => void;
}

const QuickStartAssistantContext = createContext<QuickStartAssistantContextType | undefined>(undefined);

export const useQuickStartAssistant = () => {
  const context = useContext(QuickStartAssistantContext);
  if (!context) {
    throw new Error('useQuickStartAssistant must be used within QuickStartAssistantProvider');
  }
  return context;
};

interface QuickStartAssistantProviderProps {
  children: ReactNode;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
const LAST_CONVERSATION_KEY = 'riskninja-assistant-conversation-id';

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem('riskninja-token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Reconstructs QuickStartMessage[] from the server's render-ready messages. Uses each entry's real `ts` when the server has one (rows written after the timestamp field was added); older rows fall back to "now" rather than a fabricated per-row time. */
function toQuickStartMessages(raw: Array<{ sender: 'user' | 'ai'; content: string; card?: ProposedActionCard; ts?: number }>): QuickStartMessage[] {
  const now = Date.now();
  return raw.map((m, i) => ({
    id: `qs-hydrated-${i}-${now}`,
    content: m.content,
    sender: m.sender,
    timestamp: new Date(m.ts ?? now),
    card: m.card,
  }));
}

export type KnowledgeMode = 'general' | 'agency';
const KNOWLEDGE_MODE_KEY = 'arieKnowledgeMode';

/**
 * The one assistant thread, hosted by both the Quick Start modal and the
 * floating chat bubble (see SupportChatWindow) — a tool-calling operations
 * copilot (search → propose an action card → confirm). One per-turn flag,
 * knowledgeMode: in 'agency' the server grounds the turn on the company
 * Knowledge Base (NotebookLM-style); tools stay available either way.
 *
 * Conversations are durable on the server (AssistantConversationModel) —
 * this context just tracks which conversation id is active (persisted in
 * localStorage so a refresh reopens the same thread) and hydrates its
 * messages on load. See ASSISTANT_AGENT_PLAN.md Phase 1-2.
 */
export const QuickStartAssistantProvider: React.FC<QuickStartAssistantProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<QuickStartMessage[]>([]);
  const [pendingCard, setPendingCard] = useState<ProposedActionCard | null>(null);
  const [activeRun, setActiveRun] = useState<ActiveWorkflowRun | null>(null);
  const [activePlan, setActivePlan] = useState<ActivePlan | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(() => localStorage.getItem(LAST_CONVERSATION_KEY));
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [intakeSessionId, setIntakeSessionId] = useState<string | null>(null);
  const [intakeAttachments, setIntakeAttachments] = useState<IntakeAttachment[]>([]);
  const [knowledgeMode, setKnowledgeModeState] = useState<KnowledgeMode>(
    () => (localStorage.getItem(KNOWLEDGE_MODE_KEY) === 'agency' ? 'agency' : 'general')
  );
  const setKnowledgeMode = useCallback((mode: KnowledgeMode) => {
    setKnowledgeModeState(mode);
    localStorage.setItem(KNOWLEDGE_MODE_KEY, mode);
  }, []);
  const screenContext = useScreenContext();
  const { recordUsage } = useRecentWorkflows();
  const hydratedRef = useRef(false);

  const setActiveConversationId = useCallback((id: string | null) => {
    setConversationId(id);
    if (id) {
      localStorage.setItem(LAST_CONVERSATION_KEY, id);
    } else {
      localStorage.removeItem(LAST_CONVERSATION_KEY);
    }
  }, []);

  const applyHydratedMessages = useCallback((raw: Array<{ sender: 'user' | 'ai'; content: string; card?: ProposedActionCard & { status?: string } }>) => {
    const hydrated = toQuickStartMessages(raw);
    setMessages(hydrated);
    const last = hydrated[hydrated.length - 1];
    // A card's live status (Phase 3a) says whether it's still awaiting
    // confirmation — a trailing card that was already confirmed/dismissed/
    // resolved elsewhere must not reappear as a clickable proposal.
    const lastCard = last?.card as (ProposedActionCard & { status?: string }) | undefined;
    if (lastCard && !INFORMATIONAL_CARDS.has(lastCard.tool) && (lastCard.status ?? 'proposed') === 'proposed') {
      setPendingCard(lastCard);
    } else {
      setPendingCard(null);
    }
  }, []);

  const switchConversation = useCallback(async (id: string) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/assistant/conversations/${id}`, { headers: authHeaders() });
      if (!res.ok) {
        setActiveConversationId(null);
        setMessages([]);
        setPendingCard(null);
        return;
      }
      const data = await res.json();
      setActiveConversationId(id);
      applyHydratedMessages(data.messages || []);
      setActiveRun(null);
      setActivePlan(null);
    } catch {
      // Hydration failure degrades to an empty thread rather than blocking the UI.
    }
  }, [applyHydratedMessages, setActiveConversationId]);

  // Hydrate the last-active conversation once, on first mount.
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const savedId = localStorage.getItem(LAST_CONVERSATION_KEY);
    if (savedId) {
      switchConversation(savedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/assistant/conversations`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setConversations(Array.isArray(data.conversations) ? data.conversations : []);
    } catch {
      // Best-effort — the switcher just shows nothing if this fails.
    }
  }, []);

  const appendLocalNote = useCallback((content: string) => {
    setMessages(prev => [...prev, {
      id: `qs-msg-${Date.now()}`,
      content,
      sender: 'ai',
      timestamp: new Date(),
    }]);
  }, []);

  // Client-only follow-up card — no server-side AssistantActionModel row,
  // used when the client already has everything a next-step card needs
  // (e.g. the real projectId a create_folder confirm just returned) and
  // waiting for a whole extra model turn would just re-derive the same
  // thing via search_projects. Card renders with id: undefined, which
  // every ActionCard branch already treats as "skip the optional
  // confirmAction call" (see start_upload).
  const presentFollowUpCard = useCallback((content: string, card: ProposedActionCard) => {
    setMessages(prev => [...prev, {
      id: `qs-msg-${Date.now()}`,
      content,
      sender: 'ai',
      timestamp: new Date(),
      card,
    }]);
    setPendingCard(card);
  }, []);

  const dismissPendingCard = useCallback((actionId?: string) => {
    setPendingCard(null);
    if (actionId) {
      fetch(`${API_BASE_URL}/api/assistant/actions/${actionId}/dismiss`, {
        method: 'POST',
        headers: authHeaders(),
      }).catch(() => {});
    }
  }, []);

  const dismissAction = useCallback((actionId: string) => {
    fetch(`${API_BASE_URL}/api/assistant/actions/${actionId}/dismiss`, {
      method: 'POST',
      headers: authHeaders(),
    }).catch(() => {});
  }, []);

  // Starts (or reuses) the conversation's intake session — files attached in
  // chat all land in the SAME session until it's committed or cleared, so a
  // user dropping three files across two composer actions still gets one
  // intake, not three (SMART_INTAKE_PLAN.md Phase 3a).
  const ensureIntakeSession = useCallback(async (): Promise<string> => {
    if (intakeSessionId) return intakeSessionId;
    const res = await fetch(`${API_BASE_URL}/api/intake/sessions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ source: 'assistant' }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Failed to start intake');
    setIntakeSessionId(data.sessionId);
    return data.sessionId as string;
  }, [intakeSessionId]);

  const attachFilesToIntake = useCallback(async (files: File[]) => {
    if (files.length === 0) return;
    let sessionIdForUpload: string;
    try {
      sessionIdForUpload = await ensureIntakeSession();
    } catch (err) {
      appendLocalNote(err instanceof Error ? err.message : "Couldn't start the upload — please try again.");
      return;
    }

    const entries: IntakeAttachment[] = files.map((f, i) => ({
      localId: `intake-att-${Date.now()}-${i}`,
      name: f.name,
      status: 'uploading',
    }));
    setIntakeAttachments((prev) => [...prev, ...entries]);

    await Promise.all(
      files.map(async (file, i) => {
        const entry = entries[i];
        try {
          const form = new FormData();
          form.append('file', file);
          const res = await fetch(`${API_BASE_URL}/api/intake/sessions/${sessionIdForUpload}/documents`, {
            method: 'POST',
            headers: authHeaders(),
            body: form,
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data.error || 'Upload failed');
          setIntakeAttachments((prev) =>
            prev.map((a) => (a.localId === entry.localId ? { ...a, status: 'completed', documentId: data.id } : a))
          );
        } catch (err) {
          setIntakeAttachments((prev) =>
            prev.map((a) =>
              a.localId === entry.localId
                ? { ...a, status: 'error', errorMessage: err instanceof Error ? err.message : 'Upload failed' }
                : a
            )
          );
        }
      })
    );
  }, [ensureIntakeSession, appendLocalNote]);

  const removeIntakeAttachment = useCallback((localId: string) => {
    setIntakeAttachments((prev) => prev.filter((a) => a.localId !== localId));
  }, []);

  const clearIntakeSession = useCallback(() => {
    setIntakeSessionId(null);
    setIntakeAttachments([]);
  }, []);

  const confirmAction = useCallback(async (actionId: string): Promise<ConfirmActionResult> => {
    const res = await fetch(`${API_BASE_URL}/api/assistant/actions/${actionId}/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || 'That action could not be completed.');
    }
    return data as ConfirmActionResult;
  }, []);

  const appendAssistantNote = useCallback((content: string) => {
    appendLocalNote(content);
    // Persist so the note survives a refresh and a follow-up turn ("how did
    // it go?") can see it happened server-side. Only for client-originated
    // notes the server doesn't already know about (e.g. a network failure) —
    // confirmAction's own outcomes are already persisted by the server.
    if (conversationId) {
      fetch(`${API_BASE_URL}/api/assistant/conversations/${conversationId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ content }),
      }).catch(() => {});
    }
  }, [conversationId, appendLocalNote]);

  const logAssistantEvent = useCallback((outcome: string, tool?: string) => {
    const token = localStorage.getItem('riskninja-token');
    if (!token) return;
    fetch(`${API_BASE_URL}/api/guide/events`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ flowId: tool || null, stepId: 'action_card', outcome }),
    }).catch(() => {});
  }, []);

  const startNewConversation = useCallback(() => {
    setMessages([]);
    setPendingCard(null);
    setActiveRun(null);
    setActivePlan(null);
    setActiveConversationId(null);
    setIntakeSessionId(null);
    setIntakeAttachments([]);
  }, [setActiveConversationId]);

  // The workflow itself runs and resolves server-side (Phase 3a) — the
  // browser only polls the action record for status, so closing the tab
  // mid-run no longer loses the eventual outcome (the conversation gets it
  // via the executor's own completion hook either way).
  const POLL_INTERVAL_MS = 3000;
  // ~27.5 minutes — must exceed the backend's own poll budget (25 min, see
  // JOB_POLL_MAX_ATTEMPTS in assistantActionExecutor.ts) with room to spare,
  // since this poller is just reading GET /actions/:id, whose status the
  // backend poller resolves. A shorter budget here used to show "taking
  // longer than expected" for runs that were, in fact, on track to succeed.
  const POLL_MAX_ATTEMPTS = 550;

  const pollActionStatus = useCallback((actionId: string, card: ProposedActionCard, workflowType: string, attempt = 0) => {
    setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/assistant/actions/${actionId}`, { headers: authHeaders() });
        if (!res.ok) {
          setActiveRun({ card, status: 'failed', error: 'Lost track of that run — check the Reports tab.' });
          return;
        }
        const data = await res.json();
        if (data.status === 'completed') {
          recordUsage(workflowType);
          setActiveRun({ card, status: 'completed', result: data.result as WorkflowResult });
          return;
        }
        if (data.status === 'failed') {
          setActiveRun({ card, status: 'failed', error: data.result?.error || 'The workflow failed to complete.' });
          return;
        }
        if (attempt >= POLL_MAX_ATTEMPTS) {
          setActiveRun({ card, status: 'failed', error: 'This is taking much longer than usual — it may still be running. Check the Reports tab in a few minutes.' });
          return;
        }
        pollActionStatus(actionId, card, workflowType, attempt + 1);
      } catch {
        if (attempt < POLL_MAX_ATTEMPTS) pollActionStatus(actionId, card, workflowType, attempt + 1);
      }
    }, POLL_INTERVAL_MS);
  }, [recordUsage]);

  const runOpenWorkflowCard = useCallback((card: ProposedActionCard) => {
    const { workflowType } = card.args as { workflowType: string };
    if (!card.id) {
      setActiveRun({ card, status: 'failed', error: 'This card is missing its action id — please ask again.' });
      return;
    }
    setActiveRun({ card, status: 'running' });
    confirmAction(card.id)
      .then((data) => {
        if (data.status === 'completed') {
          recordUsage(workflowType);
          setActiveRun({ card, status: 'completed', result: data.result as WorkflowResult });
        } else if (data.status === 'failed') {
          setActiveRun({ card, status: 'failed', error: data.error || 'The workflow failed to complete.' });
        } else {
          pollActionStatus(card.id!, card, workflowType);
        }
      })
      .catch((err) => {
        setActiveRun({ card, status: 'failed', error: err instanceof Error ? err.message : 'The workflow failed to complete.' });
      });
  }, [confirmAction, pollActionStatus, recordUsage]);

  // Reads a `step_update`/`done` SSE stream (plan approve/resume share this
  // shape) and folds each step_update into activePlan's step list as it
  // arrives — the checklist UI updates live as the server works through the
  // plan (Phase 3b).
  const streamPlanEvents = useCallback(async (url: string) => {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() } });
    if (!res.ok || !res.body) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'That plan could not be run.');
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalStatus: string | null = null;

    const handleEvent = (raw: string) => {
      if (!raw.startsWith('data: ')) return;
      try {
        const data = JSON.parse(raw.slice(6));
        if (data.type === 'step_update') {
          setActivePlan((prev) => {
            if (!prev) return prev;
            const steps = prev.steps.slice();
            const existing = steps[data.stepIndex] || { tool: '', args: {}, description: data.description, status: 'pending' as const };
            steps[data.stepIndex] = { ...existing, status: data.status, description: data.description ?? existing.description, result: data.result, error: data.error };
            return { ...prev, steps, status: 'running' };
          });
        } else if (data.type === 'done') {
          finalStatus = data.status;
        }
      } catch {
        // Ignore parse errors
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        if (buffer.trim()) handleEvent(buffer.trim());
        break;
      }
      const chunk = decoder.decode(value, { stream: true });
      buffer += chunk;
      const events = buffer.split('\n\n');
      buffer = events.pop() || '';
      for (const event of events) {
        if (event.trim()) handleEvent(event.trim());
      }
    }

    setActivePlan((prev) => (prev ? { ...prev, status: (finalStatus as ActivePlan['status']) || prev.status } : prev));
  }, []);

  const approvePlanCard = useCallback((card: ProposedActionCard) => {
    if (!card.id) return;
    const { title, steps } = card.args as { title: string; steps: Array<{ tool: string; args: Record<string, any>; description: string }> };
    setActivePlan({
      card,
      title,
      steps: steps.map((s) => ({ ...s, status: 'pending' as const })),
      status: 'running',
    });
    streamPlanEvents(`${API_BASE_URL}/api/assistant/actions/${card.id}/confirm`).catch((err) => {
      setActivePlan((prev) => (prev ? { ...prev, status: 'failed' } : prev));
      appendLocalNote(err instanceof Error ? err.message : 'That plan could not be run.');
    });
  }, [streamPlanEvents, appendLocalNote]);

  const resumePlanCard = useCallback(() => {
    setActivePlan((prev) => {
      if (!prev?.card.id) return prev;
      streamPlanEvents(`${API_BASE_URL}/api/assistant/actions/${prev.card.id}/resume-plan`).catch((err) => {
        setActivePlan((p) => (p ? { ...p, status: 'failed' } : p));
        appendLocalNote(err instanceof Error ? err.message : 'That plan could not be resumed.');
      });
      return { ...prev, status: 'running' };
    });
  }, [streamPlanEvents, appendLocalNote]);

  const sendMessage = useCallback(async (message: string) => {
    // The previous pendingCard (if any) is left alone here — it only stops
    // being actionable when the user confirms/dismisses it or a NEW card
    // arrives below (see `if (card) { setPendingCard(card); ... }`). A plain
    // follow-up question must not silently retract a card still on the table.
    // Snapshot the composer's attachments onto the sent message for display,
    // then clear the draft — the session itself (intakeSessionId) stays
    // active across turns until commit_intake completes or clearIntakeSession
    // is called, so files attached across several messages share one intake.
    const sentAttachments = intakeAttachments;
    setIntakeAttachments([]);

    const userMessage: QuickStartMessage = {
      id: `qs-msg-${Date.now()}`,
      content: message,
      sender: 'user',
      timestamp: new Date(),
      attachments: sentAttachments.length > 0 ? sentAttachments : undefined,
    };
    setMessages(prev => [...prev, userMessage]);

    const aiMessage: QuickStartMessage = {
      id: `qs-msg-${Date.now() + 1}`,
      content: '',
      sender: 'ai',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, aiMessage]);

    try {
      const token = localStorage.getItem('riskninja-token');
      if (!token) throw new Error('Authentication required');

      const apiUrl = process.env.REACT_APP_API_URL || '';

      const requestBody = {
        message,
        clientContext: screenContext,
        conversationId: conversationId || undefined,
        intakeSessionId: intakeSessionId || undefined,
        knowledgeMode,
      };

      const response = await fetch(`${apiUrl}/api/assistant/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) throw new Error('Failed to get response');

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      let card: ProposedActionCard | null = null;
      let buffer = '';

      const handleEvent = (raw: string) => {
        if (!raw.startsWith('data: ')) return;
        try {
          const data = JSON.parse(raw.slice(6));
          if (data.type === 'conversation' && data.conversationId) {
            setActiveConversationId(data.conversationId);
          } else if (data.type === 'tool_activity' && data.label) {
            setMessages(prev => prev.map(msg =>
              msg.id === aiMessage.id ? { ...msg, activity: data.label } : msg
            ));
          } else if (data.type === 'content' && data.content) {
            fullContent += data.content;
            // Clear any stale "Searching…" activity label the moment real
            // text starts arriving — it would otherwise keep showing below
            // the streamed reply until the whole turn finishes (Phase 3a).
            setMessages(prev => prev.map(msg =>
              msg.id === aiMessage.id ? { ...msg, content: fullContent, isStreaming: true, activity: undefined } : msg
            ));
          } else if (data.type === 'action_card' && data.card) {
            card = data.card as ProposedActionCard;
          }
        } catch (e) {
          // Ignore parse errors
        }
      };

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.trim()) handleEvent(buffer.trim());
            break;
          }
          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;
          const events = buffer.split('\n\n');
          buffer = events.pop() || '';
          for (const event of events) {
            if (event.trim()) handleEvent(event.trim());
          }
        }
      }

      setMessages(prev => prev.map(msg =>
        msg.id === aiMessage.id ? { ...msg, content: fullContent, isStreaming: false, card: card || undefined, activity: undefined } : msg
      ));

      // A generated-file card has nothing to confirm — never hold it as pending.
      if (card && (card as ProposedActionCard).tool !== 'download_file') {
        setPendingCard(card);
        logAssistantEvent('card_proposed', (card as ProposedActionCard).tool);
      }
    } catch (error) {
      console.error('Quick Start assistant error:', error);
      setMessages(prev => prev.map(msg =>
        msg.id === aiMessage.id ? { ...msg, content: 'Sorry, I encountered an error. Please try again.', isStreaming: false, activity: undefined } : msg
      ));
    }
  }, [screenContext, logAssistantEvent, conversationId, setActiveConversationId, intakeSessionId, intakeAttachments, knowledgeMode]);

  const value: QuickStartAssistantContextType = {
    messages,
    pendingCard,
    sendMessage,
    appendAssistantNote,
    appendLocalNote,
    presentFollowUpCard,
    confirmAction,
    dismissAction,
    dismissPendingCard,
    clear: startNewConversation,
    startNewConversation,
    conversationId,
    conversations,
    loadConversations,
    switchConversation,
    activeRun,
    runOpenWorkflowCard,
    activePlan,
    approvePlanCard,
    resumePlanCard,
    intakeSessionId,
    knowledgeMode,
    setKnowledgeMode,
    intakeAttachments,
    attachFilesToIntake,
    removeIntakeAttachment,
    clearIntakeSession,
  };

  return (
    <QuickStartAssistantContext.Provider value={value}>
      {children}
    </QuickStartAssistantContext.Provider>
  );
};

export default QuickStartAssistantContext;
